@import '../../utils/interaction-fill.css';
@import '../../utils/focus-ring.css';
@import '../../utils/control-density.css';
@import '../../utils/control-parts.css';
@import '../../styles/control-elevation.css';

:host {
  display: block;
  box-sizing: border-box;
  container-type: inline-size;
  -webkit-user-select: none;
  user-select: none;
  --ds-card-overview-metric-min: calc(var(--dimension-size-400) * 5);
  --ds-card-overview-divider-width: var(--dimension-stroke-width-012);
  --ds-card-overview-divider-color: var(--color-always-dark-border-tertiary);
  color: var(--color-always-dark-foreground-primary);
}

.card-overview__surface {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border-radius: var(--dimension-radius-125);
  background: var(--color-always-dark-background);
  color: var(--color-always-dark-foreground-primary);
  overflow-anchor: none;
}

.card-overview__clip {
  height: 100%;
  min-width: 0;
  border-radius: inherit;
  background: inherit;
  overflow: hidden;
}

:host(.card-overview--scroll-collapsing) {
  height: var(--ds-card-overview-expanded-height);
  pointer-events: none;
}

:host(.card-overview--scroll-collapsing) .card-overview__surface {
  height: var(--ds-card-overview-visible-height);
  pointer-events: auto;
  will-change: height;
}

:host(.card-overview--scroll-collapsing) .card-overview__body {
  transform: translateY(calc(-1 * var(--ds-card-overview-content-offset)));
  will-change: transform;
}

.card-overview__layout {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.card-overview__summary {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  min-width: 0;
  background: var(--color-always-dark-background);
}

.card-overview__header {
  display: flex;
  position: relative;
  align-items: center;
  gap: 0;
  width: 100%;
  height: var(--dimension-size-600);
  min-width: 0;
  padding: var(--dimension-space-100);
  box-sizing: border-box;
}

.card-overview__header::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  inset-block-end: 0;
  height: var(--ds-card-overview-divider-width);
  background: var(--ds-card-overview-divider-color);
  pointer-events: none;
}

.card-overview__period {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  gap: 0;
  min-width: 0;
  min-height: var(--ds-control-height);
  overflow: hidden;
  white-space: nowrap;
}

.card-overview__period-current,
.card-overview__period-comparison {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-overview__period-current {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
}

.card-overview__period-fixed {
  flex: 0 1 auto;
  min-width: 0;
}

.card-overview__period-comparison {
  flex: 0 0 auto;
}

.card-overview__filter {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  min-width: 0;
}

.card-overview__body {
  display: flex;
  position: relative;
  z-index: 0;
  flex-direction: column;
  min-width: 0;
  transform: translateY(0);
}

.card-overview__metrics {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: repeat(
    var(--ds-card-overview-grid-columns, auto-fit),
    minmax(min(100%, var(--ds-card-overview-metric-min)), 1fr)
  );
  align-content: stretch;
  min-width: 0;
  overflow: clip;
}

.card-overview__score,
.card-overview__metric {
  display: flex;
  min-width: 0;
  padding: var(--dimension-space-100);
  box-shadow:
    var(--ds-card-overview-divider-width) 0 0 0 var(--ds-card-overview-divider-color),
    0 var(--ds-card-overview-divider-width) 0 0 var(--ds-card-overview-divider-color);
  box-sizing: border-box;
}

.card-overview__score-content,
.card-overview__metric-action {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  padding: var(--dimension-space-075);
  border-radius: var(--dimension-radius-025);
  box-sizing: border-box;
}

.card-overview__score-content {
  align-items: center;
  justify-content: flex-start;
  /*
   * Sits the fill 16px from the cell's left edge, on top of the 8px cell padding.
   * Metric cells keep the shared inner inset because their content is text; the
   * fill is a filled box and needs more room around it to read as inset.
   */
  padding-inline-start: var(--dimension-space-100);
  gap: var(--dimension-space-100);
}

/*
 * The level fill wraps the score figure alone. The trend sits outside it so it
 * keeps the same color and baseline as every other metric cell, and is never
 * held to the fill's contrast.
 */
.card-overview__score-badge {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  /*
   * The padding gives the fill its own breathing room around the figure, and the
   * matching negative margin keeps that padding out of layout so the cell still
   * measures the same as a metric cell.
   */
  margin-block: calc(-1 * var(--dimension-space-050));
  padding: var(--dimension-space-050);
  border-radius: var(--dimension-radius-025);
  background: var(--color-always-dark-interaction-active-brand);
  color: var(--color-always-dark-foreground-primary);
}

.card-overview__score-badge--fair {
  background: var(--color-safety-score-background-fair);
  color: var(--color-safety-score-foreground-on-fair);
}

.card-overview__score-badge--good {
  background: var(--color-safety-score-background-good);
  color: var(--color-safety-score-foreground-on-good);
}

.card-overview__score-badge--excellent {
  background: var(--color-safety-score-background-excellent);
  color: var(--color-safety-score-foreground-on-excellent);
}

/* Mirrors .card-overview__metric-content so labels and trends line up across cells. */
.card-overview__score-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--dimension-space-050);
  min-width: 0;
}

.card-overview__score-label-spacer {
  visibility: hidden;
}

/*
 * The display line box is taller than the figure needs, so it is cropped to keep
 * the fill from driving the cell taller than a metric cell's label-plus-figure
 * column.
 */
.card-overview__score-value,
.card-overview__score-badge ds-skeleton {
  flex: 0 0 auto;
  margin-block: calc(-1 * var(--dimension-space-100));
}

.card-overview__score-content--error {
  align-items: center;
  justify-content: center;
  /* No fill to align, so this box keeps the metric cells' inner inset. */
  padding-inline-start: var(--dimension-space-075);
  border: var(--ds-card-overview-divider-width) solid
    var(--color-always-dark-border-tertiary);
  background: transparent;
  color: var(--color-always-dark-foreground-negative);
  text-align: center;
}

.card-overview__metric-action {
  flex-direction: column;
  justify-content: center;
  gap: var(--dimension-space-050);
}

.card-overview__metric-action[role='button'] {
  cursor: default;
}

.card-overview__metric-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  gap: var(--dimension-space-050);
  min-width: 0;
}

.card-overview__metric-label {
  min-width: 0;
}

.card-overview__metric-figure {
  display: flex;
  align-items: baseline;
  gap: var(--dimension-space-050);
  min-width: 0;
}

.card-overview__trend {
  flex: 0 0 auto;
  white-space: nowrap;
}

:host(.card-overview--stacked) .card-overview__metrics {
  grid-template-columns: minmax(0, 1fr);
}

:host(.card-overview--compact) {
  height: var(--dimension-size-600);
  min-height: var(--dimension-size-600);
}

:host(.card-overview--compact) .card-overview__surface,
:host(.card-overview--compact) .card-overview__clip,
:host(.card-overview--compact) .card-overview__layout,
:host(.card-overview--compact) .card-overview__summary {
  width: 100%;
  height: 100%;
}

:host(.card-overview--compact) .card-overview__header::after {
  display: none;
}

@media (forced-colors: active) {
  .card-overview__header::after {
    background: var(--ds-forced-color-content);
  }

  .card-overview__score-badge,
  .card-overview__score-content--error {
    border: var(--dimension-stroke-width-012) solid var(--ds-forced-color-content);
    background: var(--ds-forced-color-surface);
    color: var(--ds-forced-color-content);
  }
}
